import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Card, Button, Input, Select, Alert, Loading } from '../components/common'
import { spkService } from '../services/spkService'
import { masterService, bookService, schoolService } from '../services/masterService'

function SPKCreatePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [schools, setSchools] = useState([])
  const [vendors, setVendors] = useState([])
  const [books, setBooks] = useState([])
  const [selectedBook, setSelectedBook] = useState(null)

  const [formData, setFormData] = useState({
    book_id: '',
    cetak_ke: 1,
    judul: '',
    kode_buku: '',
    jenjang: '',
    qty: '',
    qty_reguler: '',
    deadline: '',
    priority: 'normal',
    mesin_isi: 'offset',
    mesin_cover: 'offset',
    ukuran_isi: 'A4',
    ukuran_cover: 'A4',
    kertas_isi: 'HVS 70gr',
    kertas_cover: 'Art Paper 210gr',
    halaman_isi: '',
    laminasi: 'glossy',
    binding_type: 'perfect_binding',
    catatan: '',
    customSchools: [],
  })

  useEffect(() => {
    fetchMasterData()
    if (isEdit) {
      fetchSPKData()
    }
  }, [id])

  const fetchMasterData = async () => {
    try {
      const [schoolsRes, vendorsRes, booksRes] = await Promise.all([
        masterService.getSchools(),
        masterService.getVendors(),
        masterService.getBooks(),
      ])
      if (schoolsRes.success) setSchools(schoolsRes.data.schools || [])
      if (vendorsRes.success) setVendors(vendorsRes.data.vendors || [])
      if (booksRes.success) setBooks(booksRes.data.books || [])
    } catch (err) {
      console.error('Error fetching master data:', err)
    }
  }

  // Handle book selection
  const handleBookSelect = async (bookId) => {
    if (!bookId) {
      setSelectedBook(null)
      setFormData(prev => ({
        ...prev,
        book_id: '',
        cetak_ke: 1,
        judul: '',
        kode_buku: '',
        jenjang: '',
        halaman_isi: '',
        customSchools: [],
      }))
      return
    }

    const book = books.find(b => b.id === parseInt(bookId))
    if (book) {
      setSelectedBook(book)

      // Get next cetak_ke + school needs for this book in parallel
      try {
        const [cetakRes, schoolNeedsRes] = await Promise.all([
          bookService.getNextCetakKe(bookId),
          bookService.getSchoolNeeds(bookId),
        ])
        const nextCetak = cetakRes.data?.nextCetakKe || 1
        const judulWithCetak = `${book.judul} [c${nextCetak}]`

        // Auto-populate custom schools from book needs
        const autoCustomSchools = (schoolNeedsRes.data?.items || []).map(item => ({
          school_code: item.school_code,
          school_name: item.school_name,
          qty: item.qty.toString(),
          custom_type: 'cover_only',
        }))

        setFormData(prev => ({
          ...prev,
          book_id: bookId,
          cetak_ke: nextCetak,
          judul: judulWithCetak,
          kode_buku: book.kode || '',
          jenjang: book.jenjang || '',
          halaman_isi: book.jumlah_halaman || prev.halaman_isi,
          ukuran_isi: book.ukuran || prev.ukuran_isi,
          ukuran_cover: book.ukuran || prev.ukuran_cover,
          kertas_isi: book.jenis_kertas_isi || prev.kertas_isi,
          kertas_cover: book.jenis_kertas_cover || prev.kertas_cover,
          customSchools: autoCustomSchools,
        }))
      } catch (err) {
        console.error('Error getting book data:', err)
        setFormData(prev => ({
          ...prev,
          book_id: bookId,
          judul: `${book.judul} [c1]`,
          kode_buku: book.kode || '',
          jenjang: book.jenjang || '',
          cetak_ke: 1,
          halaman_isi: book.jumlah_halaman || prev.halaman_isi,
          customSchools: [],
        }))
      }
    }
  }

  // Handle cetak_ke change
  const handleCetakKeChange = (value) => {
    const cetakKe = parseInt(value) || 1
    if (selectedBook) {
      const judulWithCetak = `${selectedBook.judul} [c${cetakKe}]`
      setFormData(prev => ({ ...prev, cetak_ke: cetakKe, judul: judulWithCetak }))
    } else {
      setFormData(prev => ({ ...prev, cetak_ke: cetakKe }))
    }
  }

  const fetchSPKData = async () => {
    try {
      setLoading(true)
      const response = await spkService.getById(id)
      if (response.success) {
        const spk = response.data.spk
        setFormData({
          judul: spk.judul || '',
          qty: spk.qty || '',
          qty_reguler: spk.qty_reguler || '',
          deadline: spk.deadline ? spk.deadline.split('T')[0] : '',
          priority: spk.priority || 'normal',
          mesin_isi: spk.mesin_isi || 'offset',
          mesin_cover: spk.mesin_cover || 'offset',
          ukuran_isi: spk.ukuran_isi || 'A4',
          ukuran_cover: spk.ukuran_cover || 'A4',
          kertas_isi: spk.kertas_isi || '',
          kertas_cover: spk.kertas_cover || '',
          halaman_isi: spk.halaman_isi || '',
          laminasi: spk.laminasi || 'glossy',
          binding_type: spk.binding_type || 'perfect_binding',
          catatan: spk.catatan || '',
          customSchools: spk.customSchools || [],
        })
      }
    } catch (err) {
      setError('Gagal memuat data SPK')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleAddCustomSchool = () => {
    setFormData(prev => ({
      ...prev,
      customSchools: [
        ...prev.customSchools,
        { school_code: '', school_name: '', qty: '', custom_type: 'cover_only' }
      ]
    }))
  }

  const handleRemoveCustomSchool = (index) => {
    setFormData(prev => ({
      ...prev,
      customSchools: prev.customSchools.filter((_, i) => i !== index)
    }))
  }

  const handleCustomSchoolChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      customSchools: prev.customSchools.map((cs, i) => 
        i === index ? { ...cs, [field]: value } : cs
      )
    }))
  }

  const handleSchoolSelect = (index, schoolCode) => {
    const school = schools.find(s => s.code === schoolCode)
    if (!school) return
    setFormData(prev => ({
      ...prev,
      customSchools: prev.customSchools.map((cs, i) =>
        i === index ? { ...cs, school_code: schoolCode, school_name: school.name } : cs
      )
    }))
  }

  const calculateQtyReguler = () => {
    const totalQty = parseInt(formData.qty) || 0
    const customQty = formData.customSchools.reduce((sum, cs) => sum + (parseInt(cs.qty) || 0), 0)
    return totalQty - customQty
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validation
    if (!formData.judul || !formData.qty || !formData.deadline) {
      setError('Judul, Qty, dan Deadline harus diisi')
      return
    }

    const qtyReguler = calculateQtyReguler()
    if (qtyReguler < 0) {
      setError('Qty custom tidak boleh melebihi total qty')
      return
    }

    setSaving(true)
    try {
      const payload = {
        ...formData,
        book_id: formData.book_id ? parseInt(formData.book_id) : null,
        cetak_ke: formData.cetak_ke ? parseInt(formData.cetak_ke) : 1,
        qty: parseInt(formData.qty),
        qty_reguler: qtyReguler,
        halaman_isi: parseInt(formData.halaman_isi) || null,
      }

      const response = isEdit
        ? await spkService.update(id, payload)
        : await spkService.create(payload)

      if (response.success) {
        navigate(`/spk/${response.data.spk?.id || id}`)
      } else {
        setError(response.message || 'Gagal menyimpan SPK')
      }
    } catch (err) {
      setError('Terjadi kesalahan saat menyimpan')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Loading text="Memuat data..." />
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Link to="/spk" className="text-gray-500 hover:text-gray-700 text-sm">
          ← Kembali ke Daftar SPK
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">
          {isEdit ? 'Edit SPK' : 'Buat SPK Baru'}
        </h1>
      </div>

      {error && (
        <Alert type="error" onClose={() => setError('')}>{error}</Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Info */}
        <Card title="📋 Informasi Dasar" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Book Selection */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Pilih Buku *</label>
              <select
                value={formData.book_id}
                onChange={(e) => handleBookSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Pilih dari Master Buku --</option>
                {[...books].sort((a, b) => {
                  const aPrinted = (a.last_cetak || 0) > 0 ? 1 : 0
                  const bPrinted = (b.last_cetak || 0) > 0 ? 1 : 0
                  if (aPrinted !== bPrinted) return aPrinted - bPrinted
                  const aKelas = parseInt(a.kelas) || 99
                  const bKelas = parseInt(b.kelas) || 99
                  if (aKelas !== bKelas) return aKelas - bKelas
                  return (a.judul || '').localeCompare(b.judul || '')
                }).map(book => (
                  <option key={book.id} value={book.id}>
                    [{book.kode}] {book.judul} - {book.jenjang} {book.kelas ? `Kelas ${book.kelas}` : ''}
                    {book.last_cetak > 0 ? ` (sudah cetak ${book.last_cetak}x)` : ''}
                  </option>
                ))}
              </select>
              {books.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  Belum ada data buku. <Link to="/books" className="text-blue-600 hover:underline">Tambah di Master Buku</Link>
                </p>
              )}
            </div>

            {/* Cetak Ke - only show if book is selected */}
            {formData.book_id && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cetak Ke</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.cetak_ke}
                    onChange={(e) => handleCetakKeChange(e.target.value)}
                    min="1"
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-gray-500 text-sm">
                    Format: {selectedBook?.judul || 'Judul'} [c{formData.cetak_ke}]
                  </span>
                </div>
              </div>
            )}

            {/* Kode Buku & Jenjang - Auto filled when book selected */}
            {formData.book_id && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kode Buku</label>
                  <input
                    type="text"
                    value={formData.kode_buku}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jenjang</label>
                  <input
                    type="text"
                    value={formData.jenjang}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                  />
                </div>
              </>
            )}

            {/* Selected Book Info */}
            {selectedBook && (
              <div className="md:col-span-2 bg-indigo-50 p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-indigo-800 font-medium">📚 {selectedBook.judul}</span>
                  {selectedBook.kelas && <span className="text-indigo-600 text-sm">Kelas {selectedBook.kelas}</span>}
                </div>
                <div className="text-sm text-indigo-600 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {selectedBook.mapel && <span>📘 {selectedBook.mapel}</span>}
                  {selectedBook.jumlah_halaman && <span>📄 {selectedBook.jumlah_halaman} hal</span>}
                  {selectedBook.ukuran && <span>📐 {selectedBook.ukuran}</span>}
                  {selectedBook.penulis && <span>✍️ {selectedBook.penulis}</span>}
                </div>
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Judul SPK *</label>
              <input
                type="text"
                name="judul"
                value={formData.judul}
                onChange={handleChange}
                disabled={!!formData.book_id}
                placeholder="Contoh: Matematika Kelas 7 [c1]"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${formData.book_id ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.book_id ? 'Judul otomatis terisi dari buku yang dipilih' : 'Pilih buku terlebih dahulu'}
              </p>
            </div>

            <Input
              label="Total Qty (Jumlah Cetak) *"
              name="qty"
              type="number"
              value={formData.qty}
              onChange={handleChange}
              placeholder="1000"
            />
            <Input
              label="Deadline *"
              name="deadline"
              type="date"
              value={formData.deadline}
              onChange={handleChange}
            />
            <Select
              label="Priority"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              options={[
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
                { value: 'high', label: 'High' },
                { value: 'urgent', label: 'Urgent 🚨' },
              ]}
            />
            <Input
              label="Jumlah Halaman Isi"
              name="halaman_isi"
              type="number"
              value={formData.halaman_isi}
              onChange={handleChange}
              placeholder="100"
            />
          </div>

          {/* Custom Schools - auto-populated from book needs */}
          {formData.customSchools.length > 0 && (
            <div className="mt-5 border-t pt-4">
              <h4 className="font-semibold text-gray-800 mb-3">Sekolah Custom</h4>
              <div className="bg-purple-50 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-purple-100 text-purple-800">
                      <th className="text-left px-4 py-2">No</th>
                      <th className="text-left px-4 py-2">Sekolah</th>
                      <th className="text-right px-4 py-2 w-32">Qty</th>
                      <th className="text-center px-4 py-2 w-40">Tipe</th>
                      <th className="text-center px-4 py-2 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.customSchools.map((cs, index) => (
                      <tr key={index} className="border-t border-purple-200">
                        <td className="px-4 py-2 text-gray-600">{index + 1}</td>
                        <td className="px-4 py-2 font-medium text-gray-800">
                          {cs.school_name ? (
                            cs.school_name
                          ) : (
                            <select
                              value={cs.school_code}
                              onChange={(e) => handleSchoolSelect(index, e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">-- Pilih Sekolah --</option>
                              {schools.map(s => (
                                <option key={s.code} value={s.code}>{s.name}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number"
                            value={cs.qty}
                            onChange={(e) => handleCustomSchoolChange(index, 'qty', e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-right focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-2 text-center">
                          <select
                            value={cs.custom_type}
                            onChange={(e) => handleCustomSchoolChange(index, 'custom_type', e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="cover_only">Cover Only</option>
                            <option value="cover_sisipan">Cover + Sisipan</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveCustomSchool(index)}
                            className="text-red-400 hover:text-red-600"
                            title="Hapus"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-purple-300 bg-purple-100">
                      <td colSpan="2" className="px-4 py-2 font-semibold text-purple-800">Total Custom</td>
                      <td className="px-4 py-2 text-right font-bold text-purple-800">
                        {formData.customSchools.reduce((sum, cs) => sum + (parseInt(cs.qty) || 0), 0).toLocaleString()}
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <button
                type="button"
                onClick={handleAddCustomSchool}
                className="mt-2 text-sm text-purple-600 hover:text-purple-800"
              >
                + Tambah sekolah lain
              </button>
            </div>
          )}

          {/* Qty Reguler */}
          {formData.qty && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg flex items-center justify-between">
              <span className="font-medium text-blue-800">Qty Reguler (sisa)</span>
              <span className={`font-bold text-xl ${calculateQtyReguler() < 0 ? 'text-red-600' : 'text-blue-800'}`}>
                {calculateQtyReguler().toLocaleString()}
              </span>
            </div>
          )}
          {calculateQtyReguler() < 0 && (
            <p className="text-red-500 text-sm mt-1">Qty custom melebihi total qty!</p>
          )}
        </Card>

        {/* Print Specs */}
        <Card title="🖨️ Spesifikasi Cetak" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ISI */}
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-3">📄 Cetak ISI</h4>
              <div className="space-y-3">
                <Select
                  label="Mesin"
                  name="mesin_isi"
                  value={formData.mesin_isi}
                  onChange={handleChange}
                  options={[
                    { value: 'offset', label: 'Offset' },
                    { value: 'comcolor', label: 'Comcolor' },
                  ]}
                />
                <Select
                  label="Ukuran"
                  name="ukuran_isi"
                  value={formData.ukuran_isi}
                  onChange={handleChange}
                  options={[
                    { value: 'A4', label: 'A4' },
                    { value: 'A5', label: 'A5' },
                    { value: 'B5', label: 'B5' },
                    { value: 'F4', label: 'F4/Folio' },
                  ]}
                />
                <Input
                  label="Jenis Kertas"
                  name="kertas_isi"
                  value={formData.kertas_isi}
                  onChange={handleChange}
                  placeholder="HVS 70gr"
                />
              </div>
            </div>

            {/* COVER */}
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-3">🎨 Cetak COVER</h4>
              <div className="space-y-3">
                <Select
                  label="Mesin"
                  name="mesin_cover"
                  value={formData.mesin_cover}
                  onChange={handleChange}
                  options={[
                    { value: 'offset', label: 'Offset' },
                    { value: 'digital', label: 'Digital' },
                  ]}
                />
                <Input
                  label="Jenis Kertas"
                  name="kertas_cover"
                  value={formData.kertas_cover}
                  onChange={handleChange}
                  placeholder="Art Paper 210gr"
                />
                <Select
                  label="Laminasi"
                  name="laminasi"
                  value={formData.laminasi}
                  onChange={handleChange}
                  options={[
                    { value: 'glossy', label: 'Glossy' },
                    { value: 'doff', label: 'Doff' },
                    { value: 'none', label: 'Tanpa Laminasi' },
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Binding */}
          <div className="mt-4">
            <Select
              label="Jenis Binding/Jilid"
              name="binding_type"
              value={formData.binding_type}
              onChange={handleChange}
              options={[
                { value: 'perfect_binding', label: 'Perfect Binding (Lem)' },
                { value: 'saddle_stitch', label: 'Saddle Stitch (Kawat)' },
                { value: 'spiral', label: 'Spiral' },
                { value: 'hardcover', label: 'Hard Cover' },
              ]}
              className="max-w-xs"
            />
          </div>
        </Card>

        {/* Notes */}
        <Card title="📝 Catatan" className="mb-6">
          <textarea
            name="catatan"
            value={formData.catatan}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Catatan tambahan untuk produksi..."
          />
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={() => navigate('/spk')}>
            Batal
          </Button>
          <Button type="submit" loading={saving}>
            {saving ? 'Menyimpan...' : isEdit ? 'Update SPK' : 'Buat SPK'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default SPKCreatePage
